import React from 'react';
import { motion } from 'framer-motion';
import { FaSpinner } from 'react-icons/fa';

const variantStyles = {
  primary: {
    base:  { background: 'linear-gradient(135deg, #E5252C, #B8161D)', color: '#fff', border: 'none' },
    hover: { boxShadow: '0 0 20px rgba(220,31,38,0.5), 0 0 40px rgba(220,31,38,0.2)' },
  },
  secondary: {
    base:  { backgroundColor: 'rgba(59,130,246,0.10)', color: '#60A5FA', border: '1px solid rgba(59,130,246,0.25)' },
    hover: { backgroundColor: 'rgba(59,130,246,0.18)', boxShadow: '0 0 16px rgba(59,130,246,0.3)' },
  },
  danger: {
    base:  { backgroundColor: 'rgba(239,68,68,0.10)', color: '#F87171', border: '1px solid rgba(239,68,68,0.25)' },
    hover: { backgroundColor: 'rgba(239,68,68,0.18)', boxShadow: '0 0 16px rgba(239,68,68,0.3)' },
  },
  success: {
    base:  { backgroundColor: 'rgba(34,197,94,0.10)', color: '#4ADE80', border: '1px solid rgba(34,197,94,0.25)' },
    hover: { backgroundColor: 'rgba(34,197,94,0.18)', boxShadow: '0 0 16px rgba(34,197,94,0.3)' },
  },
  outline: {
    base:  { backgroundColor: 'transparent', color: '#9BA3B8', border: '1px solid rgba(255,255,255,0.12)' },
    hover: { backgroundColor: 'rgba(255,255,255,0.05)', color: '#E8EAF0' },
  },
  warning: {
    base:  { backgroundColor: 'rgba(245,158,11,0.10)', color: '#FBBF24', border: '1px solid rgba(245,158,11,0.25)' },
    hover: { backgroundColor: 'rgba(245,158,11,0.18)', boxShadow: '0 0 16px rgba(245,158,11,0.3)' },
  },
};

const sizeClasses = {
  sm: 'py-1 px-3 text-xs',
  md: 'py-2 px-4 text-sm',
  lg: 'py-2.5 px-5 text-sm',
};

const Button = ({
  type = 'button',
  disabled = false,
  className = '',
  onClick,
  children,
  variant = 'primary',
  fullWidth = false,
  size = 'md',
  leftIcon,
  rightIcon,
  loading = false,
}) => {
  const styles = variantStyles[variant] || variantStyles.primary;
  const isDisabled = disabled || loading;

  const handleMouseEnter = (e) => {
    if (isDisabled) return;
    if (styles.hover.backgroundColor) e.currentTarget.style.backgroundColor = styles.hover.backgroundColor;
    if (styles.hover.background) e.currentTarget.style.background = styles.hover.background;
    if (styles.hover.boxShadow) e.currentTarget.style.boxShadow = styles.hover.boxShadow;
    if (styles.hover.color) e.currentTarget.style.color = styles.hover.color;
  };
  const handleMouseLeave = (e) => {
    if (styles.base.background) {
      e.currentTarget.style.background = styles.base.background;
      e.currentTarget.style.backgroundColor = '';
    } else {
      e.currentTarget.style.backgroundColor = styles.base.backgroundColor || '';
      e.currentTarget.style.background = '';
    }
    e.currentTarget.style.boxShadow = 'none';
    if (styles.base.color) e.currentTarget.style.color = styles.base.color;
  };

  return (
    <motion.button
      type={type}
      disabled={isDisabled}
      whileTap={isDisabled ? undefined : { scale: 0.97 }}
      whileHover={isDisabled ? undefined : { y: -1 }}
      transition={{ duration: 0.15 }}
      className={`
        flex justify-center items-center gap-2 font-medium rounded-lg
        transition-all duration-150 focus:outline-none
        disabled:opacity-40 disabled:cursor-not-allowed disabled:!shadow-none
        ${sizeClasses[size] || sizeClasses.md}
        ${fullWidth ? 'w-full' : ''}
        ${className}
      `}
      style={styles.base}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={onClick}
    >
      {loading ? (
        <FaSpinner size={14} style={{ animation: 'spin 1s linear infinite' }} />
      ) : (
        <>
          {leftIcon && <span>{leftIcon}</span>}
          {children}
          {rightIcon && <span>{rightIcon}</span>}
        </>
      )}
    </motion.button>
  );
};

export default Button;
